import React, { useState } from 'react';
import './BookingModal.css';

const BookingModal = ({ isOpen, onClose }) => {
  const [bookingForm, setBookingForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    guests: 2,
    date: '',
    time: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleFormChange = e => {
    const { name, value } = e.target;
    setBookingForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setBookingForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      guests: 2,
      date: '',
      time: '',
      message: '',
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Створюємо прихований iframe для відправки без редіректу
      const iframeName = 'booking-submit-iframe';
      let iframe = document.querySelector(`iframe[name="${iframeName}"]`);

      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.name = iframeName;
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
      }

      // Створюємо форму для Shopify
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/contact';
      form.target = iframeName; // Відправляємо в iframe
      form.style.display = 'none';

      // Додаємо поля форми
      const fields = [
        { name: 'form_type', value: 'contact' },
        { name: 'utf8', value: '✓' },
        {
          name: 'contact[name]',
          value: `${bookingForm.firstName} ${bookingForm.lastName}`,
        },
        { name: 'contact[email]', value: bookingForm.email },
        { name: 'contact[phone]', value: bookingForm.phone },
        {
          name: 'contact[subject]',
          value: `📅 Tischreservierung - ${bookingForm.date} um ${bookingForm.time}`,
        },
        {
          name: 'contact[body]',
          value: `
═══════════════════════════════════
     TISCHRESERVIERUNG
═══════════════════════════════════

👤 GAST INFORMATIONEN:
   Name: ${bookingForm.firstName} ${bookingForm.lastName}
   E-Mail: ${bookingForm.email}
   Telefon: ${bookingForm.phone}

📅 RESERVIERUNG DETAILS:
   Datum: ${bookingForm.date}
   Uhrzeit: ${bookingForm.time}
   Anzahl Gäste: ${bookingForm.guests} ${parseInt(bookingForm.guests) === 1 ? 'Person' : 'Personen'}

💬 NACHRICHT:
   ${bookingForm.message || 'Keine zusätzlichen Anmerkungen'}

═══════════════════════════════════
Bitte bestätigen Sie diese Reservierung per E-Mail oder Telefon.
          `.trim(),
        },
      ];

      fields.forEach(field => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = field.name;
        input.value = field.value;
        form.appendChild(input);
      });

      document.body.appendChild(form);

      // Відправляємо форму
      form.submit();

      // Показуємо success одразу
      setSubmitStatus('success');
      resetForm();

      // Закриваємо модалку через 2.5 секунди
      setTimeout(() => {
        setSubmitStatus(null);
        onClose();
        // Видаляємо форму
        document.body.removeChild(form);
      }, 2500);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(
        error.message ||
          'Fehler beim Senden der Reservierung. Bitte versuchen Sie es erneut.'
      );
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className='booking-modal__overlay' onClick={onClose}>
      <div
        className='booking-modal__content'
        onClick={e => e.stopPropagation()}
      >
        <div className='booking-modal__header'>
          <h2>Tisch Reservierung</h2>
          <button
            className='booking-modal__close'
            onClick={onClose}
            aria-label='Schließen'
            type='button'
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <line x1='18' y1='6' x2='6' y2='18'></line>
              <line x1='6' y1='6' x2='18' y2='18'></line>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className='booking-modal__form'>
          <div className='form-row'>
            <div className='form-group'>
              <label htmlFor='firstName'>Vorname *</label>
              <input
                type='text'
                id='firstName'
                name='firstName'
                value={bookingForm.firstName}
                onChange={handleFormChange}
                required
              />
            </div>
            <div className='form-group'>
              <label htmlFor='lastName'>Nachname *</label>
              <input
                type='text'
                id='lastName'
                name='lastName'
                value={bookingForm.lastName}
                onChange={handleFormChange}
                required
              />
            </div>
          </div>

          <div className='form-group'>
            <label htmlFor='email'>Email *</label>
            <input
              type='email'
              id='email'
              name='email'
              value={bookingForm.email}
              onChange={handleFormChange}
              placeholder='ihre@email.com'
              required
            />
          </div>

          <div className='form-group'>
            <label htmlFor='phone'>Telefon *</label>
            <input
              type='tel'
              id='phone'
              name='phone'
              value={bookingForm.phone}
              onChange={handleFormChange}
              placeholder='z.B. +41 76 123 45 67'
              required
              pattern='[+]?[0-9\s()-]{10,20}'
              title='Bitte geben Sie eine gültige Telefonnummer ein'
            />
          </div>

          <div className='form-row'>
            <div className='form-group'>
              <label htmlFor='guests'>Anzahl Gäste *</label>
              <select
                id='guests'
                name='guests'
                value={bookingForm.guests}
                onChange={handleFormChange}
                required
              >
                {[...Array(15)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} {i + 1 === 1 ? 'Person' : 'Personen'}
                  </option>
                ))}
                <option value='16+'>
                  16+ Personen (Bitte kontaktieren Sie uns)
                </option>
              </select>
            </div>
            <div className='form-group'>
              <label htmlFor='date'>Datum *</label>
              <input
                type='date'
                id='date'
                name='date'
                value={bookingForm.date}
                onChange={handleFormChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>

          <div className='form-group'>
            <label htmlFor='time'>Uhrzeit *</label>
            <select
              id='time'
              name='time'
              value={bookingForm.time}
              onChange={handleFormChange}
              required
            >
              <option value=''>Uhrzeit wählen</option>
              <optgroup label='Mittagessen (11:00 - 14:30)'>
                <option value='11:00'>11:00</option>
                <option value='11:30'>11:30</option>
                <option value='12:00'>12:00</option>
                <option value='12:30'>12:30</option>
                <option value='13:00'>13:00</option>
                <option value='13:30'>13:30</option>
                <option value='14:00'>14:00</option>
                <option value='14:30'>14:30</option>
              </optgroup>
              <optgroup label='Abendessen (17:00 - 22:30)'>
                <option value='17:00'>17:00</option>
                <option value='17:30'>17:30</option>
                <option value='18:00'>18:00</option>
                <option value='18:30'>18:30</option>
                <option value='19:00'>19:00</option>
                <option value='19:30'>19:30</option>
                <option value='20:00'>20:00</option>
                <option value='20:30'>20:30</option>
                <option value='21:00'>21:00</option>
                <option value='21:30'>21:30</option>
                <option value='22:00'>22:00</option>
                <option value='22:30'>22:30</option>
              </optgroup>
            </select>
          </div>

          <div className='form-group'>
            <label htmlFor='message'>Nachricht (optional)</label>
            <textarea
              id='message'
              name='message'
              value={bookingForm.message}
              onChange={handleFormChange}
              placeholder='Besondere Wünsche, Allergien oder Anmerkungen...'
              rows='3'
            />
          </div>

          <div className='booking-info'>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <circle cx='12' cy='12' r='10'></circle>
              <line x1='12' y1='16' x2='12' y2='12'></line>
              <line x1='12' y1='8' x2='12.01' y2='8'></line>
            </svg>
            <p>
              Nach dem Absenden erhalten Sie eine Bestätigungs-E-Mail. Wir
              werden Ihre Reservierung innerhalb von 24 Stunden bestätigen.
            </p>
          </div>

          <div className='form-actions'>
            <button type='button' onClick={onClose} className='btn-secondary'>
              Abbrechen
            </button>
            <button
              type='submit'
              disabled={isSubmitting}
              className='btn-primary'
            >
              {isSubmitting ? (
                <>
                  <span className='spinner'></span>
                  Wird gesendet...
                </>
              ) : (
                'Reservierung senden'
              )}
            </button>
          </div>
        </form>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <div className='status-overlay'>
            <div className='status-message status-success'>
              <div className='status-icon'>
                <svg
                  width='60'
                  height='60'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                >
                  <circle cx='12' cy='12' r='10'></circle>
                  <path d='M9 12l2 2 4-4'></path>
                </svg>
              </div>
              <h3>Buchung erfolgreich gesendet!</h3>
              <p>
                Vielen Dank für Ihre Reservierung.
                <br />
                Wir werden uns in Kürze bei Ihnen melden.
              </p>
              <div className='status-checkmark'>✓</div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <div className='status-overlay'>
            <div className='status-message status-error'>
              <div className='status-icon'>
                <svg
                  width='60'
                  height='60'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                >
                  <circle cx='12' cy='12' r='10'></circle>
                  <line x1='15' y1='9' x2='9' y2='15'></line>
                  <line x1='9' y1='9' x2='15' y2='15'></line>
                </svg>
              </div>
              <h3>Fehler bei der Buchung</h3>
              <p>
                Bitte versuchen Sie es erneut oder rufen Sie uns an:
                <br />
                <strong
                  style={{
                    fontSize: '1.2rem',
                    color: '#2c3e50',
                    marginTop: '0.5rem',
                    display: 'block',
                  }}
                >
                  📞 +41 43 818 29 12
                </strong>
              </p>
              <button
                className='btn-primary'
                onClick={() => setSubmitStatus(null)}
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
